import { useState } from "react";

import { IHospitalDetail } from "../../../../@types/admin/AdminHospitalDetails";
import { IWard } from "../../../../@types/admin/AdminWard";
import { useStartBulkImport } from "../../../../hooks/mutations/admin/hospitalDetails";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  AdminSelectField,
  CheckboxField,
  FieldError,
  FieldHint,
  FileUploadField,
  FilterButton,
  FilterGroup,
  FilterLabel,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../../shared";
import { CsvStructureDetails } from "./CsvStructureDetails";

interface IStartBulkImportModalProps {
  hospital: IHospitalDetail;
  wards: IWard[];
  /** Called with the queued job id so the caller can open its result. */
  onQueued: (jobId: string) => void;
  onCancel: () => void;
}

/** 1 MB — what the endpoint enforces (`[RequestSizeLimit(1_048_576)]`). */
const MAX_BYTES = 1 * 1024 * 1024;

/**
 * Queues a CSV bulk import into one ward — Retool's `modulePatientBulkImport`
 * (EN-5175).
 *
 * ⚠️ **This creates patient records in bulk.** A wrong file or a wrong ward
 * creates many bad records, and there is no bulk undo — each one has to be
 * archived individually. So the ward is chosen explicitly (never defaulted),
 * named back in the confirmation, and the operator has to tick that they have
 * checked it.
 *
 * The ward is not pre-selected on purpose. A defaulted ward is the single
 * easiest way to import fifty patients into the wrong one.
 */
export function StartBulkImportModal({
  hospital,
  wards,
  onQueued,
  onCancel,
}: IStartBulkImportModalProps) {
  const { showToast } = useToast();
  const { startBulkImport, isStartingBulkImport } = useStartBulkImport();

  const [file, setFile] = useState<File | null>(null);
  const [wardId, setWardId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [touched, setTouched] = useState(false);

  const selectedWard = wards.find((ward) => String(ward.id) === wardId);

  const errors = {
    file: file ? undefined : "Choose a CSV file.",
    wardId: wardId ? undefined : "Choose the ward these patients belong to.",
    confirmed: confirmed
      ? undefined
      : "Confirm you have checked the file and the ward.",
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const handleSubmit = async () => {
    setTouched(true);
    if (hasErrors || !file) return;

    try {
      const job = await startBulkImport({ file, wardId: Number(wardId) });
      showToast("The import was queued.", "success");
      onQueued(job.jobId);
    } catch (error) {
      // The endpoint rejects a malformed CSV with a message naming the problem.
      showToast(
        errorToastMessage(error, "There was an error starting the import."),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Import patients</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Every patient in the file is created in one ward of{" "}
          <strong>{hospital.name}</strong>.
        </ModalBodyText>

        <FilterGroup>
          <FilterLabel htmlFor="bulk-import-ward">Ward</FilterLabel>
          <AdminSelectField
            id="bulk-import-ward"
            value={wardId}
            onChange={(e) => setWardId(e.target.value)}
          >
            <option value="">Select a ward</option>
            {wards.map((ward) => (
              <option key={ward.id} value={String(ward.id)}>
                {ward.name}
              </option>
            ))}
          </AdminSelectField>
          {touched && errors.wardId && <FieldError>{errors.wardId}</FieldError>}
          <FieldHint>
            Not pre-filled on purpose — a defaulted ward is how fifty patients
            end up in the wrong one.
          </FieldHint>
        </FilterGroup>

        <FileUploadField
          label="Patient CSV"
          accept={[".csv", "text/csv"]}
          maxBytes={MAX_BYTES}
          value={file}
          onChange={setFile}
          disabled={isStartingBulkImport}
          helperText="One patient per row."
          error={touched ? errors.file : undefined}
        />

        <CsvStructureDetails />

        {selectedWard && file && (
          <WarningNotice>
            <strong>{file.name}</strong> will create patients in{" "}
            <strong>{selectedWard.name}</strong>. There is no bulk undo — a
            patient created by mistake has to be archived one at a time.
          </WarningNotice>
        )}

        <FilterGroup>
          <CheckboxField>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            I have checked the file and the ward.
          </CheckboxField>
          {touched && errors.confirmed && (
            <FieldError>{errors.confirmed}</FieldError>
          )}
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isStartingBulkImport}
            onClick={onCancel}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isStartingBulkImport}
            onClick={handleSubmit}
          >
            {isStartingBulkImport ? "Queueing..." : "Start import"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
